import { useEffect, useRef, useState } from 'react'
import { captureFix, GpsError } from '@/lib/location'
import type { MessageKey } from '@/i18n'
import { sumPaise } from '@/lib/money'
import { closeChecks, type CloseCheckKey } from '../../model/closeChecks'
import { lotPlan } from '../../model/delivery'
import { useLotFigures } from '../../pages/delivery/hooks'
import {
  Avatar,
  Btn,
  Card,
  Cta,
  Footer,
  Icon,
  ItemRow,
  SheetActions,
  Strip,
  Tick,
  V1Screen,
  V1Sheet,
  WaitCta,
  Warn,
} from '../kit'
import { C } from '../style'
import { tr, kg, money, rosterShort, shortLot, slate } from '../text'
import { Rich } from '../Rich'
import { StepHeader } from './shared'
import { useV1 } from './ctx'

export type FixTarget =
  'storage' | 'crop' | 'moisture' | 'tag' | 'seal' | 'farmerSlip' | 'paperSlip'

const NAME: Partial<Record<CloseCheckKey, MessageKey>> = {
  bags_tagged: 'gate.v1.final.tags',
  sealed: 'gate.v1.final.sealed',
  storage: 'gate.v1.final.storage',
  moisture: 'gate.v1.final.moisture',
  cards: 'gate.v1.final.cards',
  slip_no: 'gate.v1.final.slipNo',
  slip_photo: 'gate.v1.final.slipPhoto',
  agreed: 'gate.v1.final.agreed',
  location: 'gate.check.location',
}
const FIX: Partial<Record<CloseCheckKey, FixTarget>> = {
  bags_tagged: 'tag',
  sealed: 'seal',
  storage: 'storage',
  moisture: 'moisture',
  cards: 'crop',
  slip_no: 'paperSlip',
  slip_photo: 'paperSlip',
  agreed: 'farmerSlip',
}
/** The board's order: two columns, left to right. */
const ORDER: CloseCheckKey[] = [
  'bags_tagged',
  'sealed',
  'storage',
  'moisture',
  'cards',
  'slip_no',
  'slip_photo',
  'agreed',
  'location',
]

function useChecks() {
  const { d, cards } = useV1()
  const cardsOk = lotPlan(d).every(
    (l) => cards[l.stream] && cards[l.stream]!.price_paise_per_kg != null,
  )
  const all = closeChecks(d, { moistureRequired: !!d.moisture_required, sampleMinG: 0, cardsOk })
  return ORDER.flatMap((k) => all.filter((c) => c.key === k))
}

/** F4.10 — आख़िरी जाँच: every tick, each lot in its own row with its slate, then ख़रीद पक्की करें. */
export function FinalScreen({
  onBack,
  onFix,
}: {
  onBack: () => void
  onFix: (t: FixTarget) => void
}) {
  const { d } = useV1()
  const checks = useChecks()
  const failing = checks.filter((c) => !c.ok)
  const lots = useLotFigures()
  const [sheet, setSheet] = useState<null | 'confirm' | 'cancel'>(null)
  const nameOf = (k: CloseCheckKey) => tr(NAME[k] ?? 'gate.v1.final.tags')
  const live = d.bags.filter((b) => !b.withdrawn)

  return (
    <V1Screen
      header={
        <StepHeader
          step="close"
          icon="rowbags"
          title={tr('gate.v1.final.title')}
          onBack={onBack}
          onMore={() => setSheet('cancel')}
        />
      }
      footer={
        <Footer>
          {failing.length ? (
            <WaitCta>{tr('gate.v1.final.left', { name: nameOf(failing[0]!.key) })}</WaitCta>
          ) : (
            <Cta
              label={tr('gate.v1.final.close')}
              icon="rowbags"
              onClick={() => setSheet('confirm')}
            />
          )}
        </Footer>
      }
      overlay={
        sheet === 'confirm' ? (
          <ConfirmCloseSheet onClose={() => setSheet(null)} />
        ) : sheet === 'cancel' ? (
          <CancelSheet onClose={() => setSheet(null)} />
        ) : null
      }
    >
      <Card gap={8}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          {checks
            // The price is not one of the board's ticks: it shows only when a lot has none.
            .filter((c) => c.ok && c.key !== 'cards')
            .map((c) => (
              <div key={c.key} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Tick />
                <span
                  style={{
                    fontSize: 16,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  {c.key === 'agreed' && <Avatar url={d.farmer.photo_url} size={22} icon={18} />}
                  {nameOf(c.key)}
                </span>
              </div>
            ))}
        </div>
      </Card>
      {failing
        .filter((c) => c.key !== 'location')
        .map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => FIX[c.key] && onFix(FIX[c.key]!)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 10px',
              borderRadius: 12,
              background: C.badWash,
              border: `2px solid ${C.bad}`,
              color: C.ink,
              fontFamily: "'Mukta', sans-serif",
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: 4,
                background: C.bad,
                color: '#ffffff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="x" size={16} sw={3} />
            </span>
            <span
              style={{ flexGrow: 1, fontSize: 18, fontWeight: 700, lineHeight: 1.25, color: C.bad }}
            >
              {nameOf(c.key)}
            </span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontSize: 17,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {tr('gate.v1.fix')}
              <Icon name="next" size={22} sw={2.5} />
            </span>
          </button>
        ))}
      {lots.map(({ plan, figures }) => {
        const isRed = plan.stream === 'CONV'
        return (
          <ItemRow
            key={plan.id}
            lead={
              <Icon
                name={isRed ? 'redtie' : 'sack'}
                size={30}
                style={{ color: isRed ? C.ink : C.ok }}
              />
            }
            title={shortLot(plan.code)}
            line={tr('gate.v1.closeDay.lotLine', { bags: plan.bag_count, kg: kg(figures.net_g) })}
          />
        )
      })}
      {live.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 12,
            background: C.sunk,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div
            role="img"
            aria-label={tr('gate.v1.final.rowsLabel')}
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          >
            {lots.map(({ plan, bags }) => {
              const isRed = plan.stream === 'CONV'
              return (
                <div
                  key={plan.id}
                  style={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}
                >
                  <span
                    style={{
                      flexShrink: 0,
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: C.slate,
                      border: `2px solid ${C.slateEdge}`,
                      color: '#ffffff',
                      fontSize: 14,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      fontVariantNumeric: 'tabular-nums',
                      marginRight: 6,
                    }}
                  >
                    {slate(plan.code)}
                  </span>
                  {bags
                    .filter((b) => !b.withdrawn)
                    .map((b) => (
                      <span
                        key={b.id}
                        style={{ color: isRed ? C.ink : C.photoInk, display: 'flex' }}
                      >
                        <Icon name={isRed ? 'redtie' : 'sack'} size={24} sw={1.8} />
                      </span>
                    ))}
                </div>
              )
            })}
          </div>
          <span style={{ fontSize: 16, lineHeight: 1.3 }}>
            <Rich text={tr('gate.v1.final.rows')} />
          </span>
        </div>
      )}
      {d.location_required && <LocationRow />}
    </V1Screen>
  )
}

type GpsState =
  | { kind: 'idle' }
  | { kind: 'busy'; live: number | null }
  | { kind: 'error'; message: string; denied: boolean }

/**
 * ख़रीद की लोकेशन — taken here, at the end, where the purchase is closed. Starts by itself when
 * the screen opens; the watch always ends (deadline, good fix, or this screen closing), so the
 * screen never hangs. ख़रीद पक्की करें opens only once it is taken, or a supervisor allows it.
 */
export function LocationRow() {
  const { d, dispatch, requestSupervisor } = useV1()
  // Opens already locating when it starts by itself (below), so the effect sets no state.
  const autoStart = !d.purchase_gps && !d.location_skipped_by
  const [state, setState] = useState<GpsState>(
    autoStart ? { kind: 'busy', live: null } : { kind: 'idle' },
  )
  const abort = useRef<AbortController | null>(null)
  const fix = d.purchase_gps ?? null

  /**
   * One capture; the watch always ends (deadline, good fix, or abort). A retry waits longer: with
   * no internet a cold GPS needs more time for its first lock.
   */
  const run = async (maxWait = 20_000) => {
    abort.current?.abort()
    const ctl = new AbortController()
    abort.current = ctl
    try {
      const got = await captureFix({
        targetAccuracy: 20,
        maxWait,
        signal: ctl.signal,
        onProgress: (f) =>
          !ctl.signal.aborted && setState({ kind: 'busy', live: Math.round(f.accuracy_m) }),
      })
      if (ctl.signal.aborted) return
      dispatch({ type: 'purchase_gps', gps: got })
      setState({ kind: 'idle' })
    } catch (e) {
      if (ctl.signal.aborted) return
      const denied = e instanceof GpsError && e.code === 'DENIED'
      setState({
        kind: 'error',
        denied,
        message: denied ? tr('gate.v1.gps.denied') : tr('gate.v1.gps.noFix'),
      })
    }
  }
  const capture = () => {
    setState({ kind: 'busy', live: null })
    void run(45_000)
  }

  // Once, when the final screen opens; cancelled when it closes.
  useEffect(() => {
    const start = autoStart ? setTimeout(() => void run(), 0) : null
    return () => {
      if (start) clearTimeout(start)
      abort.current?.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per screen
  }, [])

  const skip = async () => {
    const sup = await requestSupervisor(tr('gate.v1.gps.supReason'))
    if (sup) dispatch({ type: 'location_skip', by: sup.user_id })
  }

  const busy = state.kind === 'busy'
  const ok = !!fix || !!d.location_skipped_by
  return (
    <Card gap={8}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 18,
            background: ok && !busy ? C.ok : busy ? C.acc : C.bad,
            color: '#ffffff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={ok && !busy ? 'ok' : busy ? 'hourglass' : 'bang'} size={20} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
          <b style={{ fontSize: 18, lineHeight: 1.25 }}>{tr('gate.check.location')}</b>
          <span role="status" style={{ fontSize: 15, color: C.muted, lineHeight: 1.3 }}>
            {busy
              ? state.live !== null
                ? tr('gate.v1.gps.holdStill', { m: state.live })
                : tr('gate.v1.gps.locating')
              : fix
                ? `${fix.lat.toFixed(6)}, ${fix.lng.toFixed(6)} · ±${Math.round(fix.accuracy_m)} m`
                : d.location_skipped_by
                  ? tr('gate.v1.gps.skipped')
                  : tr('gate.v1.gps.notYet')}
          </span>
        </span>
      </div>
      {fix?.fallback && !busy && (
        <Strip icon="bang">{tr('gate.v1.gps.fallback', { m: Math.round(fix.accuracy_m) })}</Strip>
      )}
      {state.kind === 'error' && <Strip icon="bang">{state.message}</Strip>}
      {!busy && (
        <Btn icon="undo" onClick={capture}>
          {fix ? tr('gate.v1.gps.recapture') : tr('gate.v1.gps.capture')}
        </Btn>
      )}
      {state.kind === 'error' && !ok && (
        <Btn icon="supervisor" onClick={() => void skip()}>
          {tr('gate.v1.gps.skip')}
        </Btn>
      )}
    </Card>
  )
}

/** ख़रीद पक्की करें? — after this it cannot change; the supervisor fixes mistakes. */
function ConfirmCloseSheet({ onClose }: { onClose: () => void }) {
  const { d, busy, commit } = useV1()
  const lots = useLotFigures()
  const total = sumPaise(lots.map((l) => l.figures.amount_paise))
  const bags = lots.reduce((s, l) => s + l.plan.bag_count, 0)
  const net = lots.reduce((s, l) => s + l.figures.net_g, 0)
  return (
    <V1Sheet
      title={tr('gate.v1.confirm2.title')}
      onClose={onClose}
      top={300}
      actions={
        <SheetActions>
          <Btn icon="back" onClick={onClose}>
            {tr('gate.v1.back')}
          </Btn>
          <Cta
            label={tr('gate.v1.confirm2.yes')}
            icon="ok"
            busy={busy}
            onClick={() => void commit()}
          />
        </SheetActions>
      }
    >
      <ItemRow
        padding="10px 12px"
        lead={<Avatar url={d.farmer.photo_url} size={44} icon={37} />}
        titleSize={18}
        title={`${d.farmer.name} · ${tr('gate.v1.farmerNo', { no: rosterShort(d.farmer.roster_no) })}`}
        line={`${tr('gate.v1.bags', { count: bags })} · ${tr('gate.v1.kg', { kg: kg(net) })} · ${money(total)}`}
      />
      <Warn>
        <Rich text={tr('gate.v1.confirm2.warn')} />
      </Warn>
    </V1Sheet>
  )
}

/** ख़रीद रद्द करें? (⋯ menu): nothing is bought; cut off the tags on the bags. No scans in V1. */
export function CancelSheet({ onClose }: { onClose: () => void }) {
  const { d, busy, cancel } = useV1()
  const [reason, setReason] = useState<null | 'WALK_AWAY' | 'DIVERTED_TO_DRY'>(null)
  const tags = d.bags.filter((b) => !b.withdrawn).length
  const tile = (r: 'WALK_AWAY' | 'DIVERTED_TO_DRY', label: string) => (
    <button
      type="button"
      aria-pressed={reason === r}
      onClick={() => setReason(r)}
      style={{
        position: 'relative',
        minHeight: 52,
        borderRadius: 12,
        border: reason === r ? `3px solid ${C.ink}` : `1.5px solid ${C.border}`,
        background: C.surface,
        color: C.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 10px',
        gap: 6,
        fontFamily: "'Mukta', sans-serif",
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
      }}
    >
      <b style={{ fontSize: 18 }}>{label}</b>
    </button>
  )
  return (
    <V1Sheet
      title={tr('gate.v1.cancel.title')}
      onClose={onClose}
      top={220}
      actions={
        reason ? (
          // "टैग काटे · रद्द करें" does not fit half the width: the main action takes the row.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Cta
              label={tr(tags ? 'gate.v1.cancel.cutAndCancel' : 'gate.v1.cancel.cancelNow')}
              icon={tags ? 'scissors' : 'x'}
              busy={busy}
              onClick={() => void cancel(reason)}
            />
            <Btn icon="back" onClick={onClose}>
              {tr('gate.v1.back')}
            </Btn>
          </div>
        ) : (
          <SheetActions>
            <Btn icon="back" onClick={onClose}>
              {tr('gate.v1.back')}
            </Btn>
            <WaitCta>{tr('gate.v1.pickReason')}</WaitCta>
          </SheetActions>
        )
      }
    >
      <div
        style={{
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          padding: '8px 12px',
          borderRadius: 12,
          background: C.badWash,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 10,
            background: C.surface,
            color: C.bad,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="undo" size={24} />
        </span>
        <span style={{ fontSize: 16, lineHeight: 1.35 }}>
          <b>{tr('gate.v1.cancel.nothing')}</b>
        </span>
      </div>
      {tags > 0 && (
        <ItemRow
          padding="10px 12px"
          lead={<Icon name="scissors" size={28} />}
          title={tr('gate.v1.cancel.cutTags', { count: tags })}
        />
      )}
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
        {tr('gate.v1.why')}
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        {tile('WALK_AWAY', tr('gate.v1.cancel.left'))}
        {tile('DIVERTED_TO_DRY', tr('gate.v1.cancel.dry'))}
      </div>
    </V1Sheet>
  )
}

/** ख़रीद पक्की (green) or ख़रीद रद्द, then अगला किसान. */
export function DoneScreen({
  kind,
  farmer,
  bags,
  netG,
  totalPaise,
  parts,
  onNext,
}: {
  kind: 'COMMITTED' | 'VOIDED'
  farmer: { name: string; roster_no: string }
  bags: number
  netG: number
  totalPaise: number
  parts: number
  onNext: () => void
}) {
  const ok = kind === 'COMMITTED'
  return (
    <V1Screen
      main={{ padding: '0', gap: 0 }}
      footer={
        <Footer>
          <Cta label={tr('gate.v1.done.next')} icon="user" lead onClick={onNext} />
        </Footer>
      }
    >
      <div
        style={{
          flexGrow: 1,
          background: ok ? C.ok : C.dim,
          color: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 20,
          textAlign: 'center',
        }}
      >
        <span
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            background: '#ffffff',
            color: ok ? C.ok : C.dim,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={ok ? 'ok' : 'undo'} size={76} sw={3.2} />
        </span>
        <h1 style={{ margin: 0, fontSize: 30 }}>
          {tr(ok ? 'gate.v1.done.title' : 'gate.v1.done.cancelled')}
        </h1>
        <span style={{ fontSize: 20, lineHeight: 1.4 }}>
          {farmer.name} · {tr('gate.v1.farmerNo', { no: rosterShort(farmer.roster_no) })}
          {ok && (
            <>
              <br />
              {tr('gate.v1.bags', { count: bags })} · {tr('gate.v1.kg', { kg: kg(netG) })}
            </>
          )}
        </span>
        <span
          style={{
            fontSize: 18,
            lineHeight: 1.4,
            padding: '8px 12px',
            borderRadius: 12,
            background: 'rgba(0,0,0,.18)',
          }}
        >
          {ok ? (
            <>
              {tr('gate.v1.done.value', { amount: money(totalPaise) })}
              {parts > 1 && (
                <>
                  {' · '}
                  <Rich text={tr('gate.v1.slip.parts', { count: parts }).replaceAll('**', '')} />
                </>
              )}
            </>
          ) : (
            tr('gate.v1.done.nothingBought')
          )}
        </span>
      </div>
    </V1Screen>
  )
}
